import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Mail, Lock, ArrowLeft, Eye, EyeOff, CheckCircle } from "lucide-react";
import { authAPI } from "@/services/api";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<'email' | 'otp' | 'password' | 'success'>('email');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    email: "",
    otp: "",
    newPassword: "",
    confirmPassword: "",
    resetToken: "",
    recoveryType: "" as 'unverified_user' | 'password_reset' | ''
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (error) setError("");
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.email.trim()) {
      setError("Email is required");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const response = await authAPI.forgotPassword(formData.email.trim().toLowerCase());
      
      // ✅ NEW: Handle different recovery types
      if (response.data?.recoveryType === 'unverified_user') {
        setSuccess("Account recovery email sent. Please verify your email first, then you can reset your password.");
        // Store recovery type for later use
        setFormData(prev => ({ ...prev, recoveryType: 'unverified_user' }));
      } else {
        setSuccess("If an account with that email exists, you will receive a password reset code shortly.");
        setFormData(prev => ({ ...prev, recoveryType: 'password_reset' }));
      }
      
      setCurrentStep('otp');
    } catch (err: any) {
      console.error('Forgot password error:', err);
      setError(err.response?.data?.message || 'Failed to send reset code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOTPSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.otp.trim() || formData.otp.length !== 6) {
      setError("Please enter a valid 6-digit code");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      // ✅ NEW: Handle different verification types
      if (formData.recoveryType === 'unverified_user') {
        // For unverified users: verify email first
        await authAPI.verifyEmailOnly(formData.email, formData.otp);
        setSuccess("Email verified successfully! Now requesting password reset...");
        
        // After email verification, request password reset OTP
        const resetResponse = await authAPI.forgotPassword(formData.email);
        setFormData(prev => ({ 
          ...prev, 
          recoveryType: 'password_reset',
          otp: '' // Clear OTP field for new input
        }));
        setSuccess("Password reset code sent to your email!");
        // Stay on OTP step to enter password reset OTP
      } else {
        // For verified users: normal password reset flow
        const response = await authAPI.verifyResetOTP(formData.email, formData.otp);
        setFormData(prev => ({ ...prev, resetToken: response.data.resetToken }));
        setSuccess("Reset code verified successfully!");
        setCurrentStep('password');
      }
    } catch (err: any) {
      console.error('OTP verification error:', err);
      setError(err.response?.data?.message || 'Invalid or expired reset code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.newPassword || !formData.confirmPassword) {
      setError("Please fill in both password fields");
      return;
    }

    if (formData.newPassword.length < 6) {
      setError("Password must be at least 6 characters long");
      return;
    }

    if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.newPassword)) {
      setError("Password must contain at least one uppercase letter, one lowercase letter, and one number");
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      await authAPI.resetPassword(formData.email, formData.resetToken, formData.newPassword);
      setSuccess("Password reset successfully!");
      setCurrentStep('success');
    } catch (err: any) {
      console.error('Password reset error:', err);
      setError(err.response?.data?.message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const response = await authAPI.forgotPassword(formData.email);
      
      // Handle different recovery types for resend
      if (response.data?.recoveryType === 'unverified_user') {
        setSuccess("New verification code sent to your email!");
        setFormData(prev => ({ ...prev, recoveryType: 'unverified_user' }));
      } else {
        setSuccess("New reset code sent to your email!");
        setFormData(prev => ({ ...prev, recoveryType: 'password_reset' }));
      }
    } catch (err: any) {
      console.error('Resend error:', err);
      setError(err.response?.data?.message || 'Failed to resend code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderEmailStep = () => (
    <form onSubmit={handleEmailSubmit} className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="email" className="text-sm font-medium text-gray-700">
          Email Address
        </Label>
        <div className="relative">
          <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
          <Input
            id="email"
            name="email"
            type="email"
            placeholder="Enter your email address"
            value={formData.email}
            onChange={handleInputChange}
            className="pl-10 h-11 border-border focus:border-blue-500 focus:ring-blue-500 transition-colors"
            required
          />
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md text-sm">
          {success}
        </div>
      )}

             <Button
         type="submit"
         disabled={loading}
         className="w-full h-11 bg-blue-500 hover:bg-primary text-white font-medium transition-all duration-200"
       >
         {loading ? (
           <div className="flex items-center justify-center">
             <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
             Sending Code...
           </div>
         ) : (
           "Continue"
         )}
       </Button>
    </form>
  );

  const renderOTPStep = () => (
    <form onSubmit={handleOTPSubmit} className="space-y-6">
             <div className="text-center mb-6">
         <p className="text-gray-600">
           {formData.recoveryType === 'unverified_user' 
             ? (
               <>
                 We've sent a 6-digit verification code to<br />
                 <span className="font-medium text-secondary">{formData.email}</span><br />
                 Please verify your email first, then you can reset your password.
               </>
             )
             : (
               <>
                 We've sent a 6-digit password reset code to<br />
                 <span className="font-medium text-secondary">{formData.email}</span>
               </>
             )
           }
         </p>
       </div>

      <div className="space-y-2">
        <Label htmlFor="otp" className="text-sm font-medium text-gray-700">
          {formData.recoveryType === 'unverified_user' ? 'Verification Code' : 'Reset Code'}
        </Label>
        <Input
          id="otp"
          name="otp"
          type="text"
          placeholder="Enter 6-digit code"
          value={formData.otp}
          onChange={handleInputChange}
          maxLength={6}
          className="text-center text-2xl tracking-widest h-16 border-border focus:border-blue-500 focus:ring-blue-500 transition-colors font-mono"
          required
        />
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md text-sm">
          {success}
        </div>
      )}

      <Button
        type="submit"
        disabled={loading || formData.otp.length !== 6}
        className="w-full h-11 bg-blue-500 hover:bg-primary text-white font-medium transition-all duration-200"
      >
        {loading ? (
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            Verifying...
          </div>
        ) : (
          formData.recoveryType === 'unverified_user' ? "Verify Email" : "Verify Code"
        )}
      </Button>

             <div className="text-center">
         <button
           type="button"
           onClick={handleResendCode}
           disabled={loading}
           className="text-primary hover:text-blue-700 font-medium text-sm transition-colors"
         >
           Didn't receive the code? 
           {formData.recoveryType === 'unverified_user' ? ' Resend verification code' : ' Resend reset code'}
         </button>
       </div>
    </form>
  );

  const renderPasswordStep = () => (
    <form onSubmit={handlePasswordSubmit} className="space-y-6">
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="newPassword" className="text-sm font-medium text-gray-700">
            New Password
          </Label>
          <div className="relative">
            <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <Input
              id="newPassword"
              name="newPassword"
              type={showPassword ? "text" : "password"}
              placeholder="Enter new password"
              value={formData.newPassword}
              onChange={handleInputChange}
              className="pl-10 pr-10 h-11 border-border focus:border-blue-500 focus:ring-blue-500 transition-colors"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-3 text-gray-400 hover:text-gray-600 transition-colors"
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="confirmPassword" className="text-sm font-medium text-gray-700">
            Confirm New Password
          </Label>
          <div className="relative">
            <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <Input
              id="confirmPassword"
              name="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              placeholder="Confirm new password"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              className="pl-10 pr-10 h-11 border-border focus:border-blue-500 focus:ring-blue-500 transition-colors"
              required
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-3 text-gray-400 hover:text-gray-600 transition-colors"
            >
              {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 p-4 rounded-md">
        <h4 className="font-medium text-blue-900 mb-2">Password Requirements:</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• At least 6 characters long</li>
          <li>• Contains at least one uppercase letter</li>
          <li>• Contains at least one lowercase letter</li>
          <li>• Contains at least one number</li>
        </ul>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md text-sm">
          {success}
        </div>
      )}

      <Button
        type="submit"
        disabled={loading}
        className="w-full h-11 bg-blue-500 hover:bg-primary text-white font-medium transition-all duration-200"
      >
        {loading ? (
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            Resetting Password...
          </div>
        ) : (
          "Reset Password"
        )}
      </Button>
    </form>
  );

  const renderSuccessStep = () => (
    <div className="text-center space-y-6">
      <div className="w-16 h-16 bg-primary-light rounded-full flex items-center justify-center mx-auto">
        <CheckCircle className="w-8 h-8 text-primary" />
      </div>
      
      <div>
        <h3 className="text-xl font-semibold text-secondary mb-2">Password Reset Successfully!</h3>
        <p className="text-gray-600">
          Your password has been reset successfully. You can now login with your new password.
        </p>
      </div>

      <Button
        onClick={() => navigate('/login')}
        className="w-full h-11 bg-primary hover:bg-primary text-white font-medium transition-all duration-200"
      >
        Continue to Login
      </Button>
    </div>
  );

  const getStepTitle = () => {
    switch (currentStep) {
      case 'email': return 'Forgot Password';
      case 'otp': return formData.recoveryType === 'unverified_user' ? 'Verify Email' : 'Verify Reset Code';
      case 'password': return 'Set New Password';
      case 'success': return 'Password Reset Complete';
      default: return 'Forgot Password';
    }
  };

  const getStepDescription = () => {
    switch (currentStep) {
      case 'email': return 'Enter your email address to recover your account';
      case 'otp': return formData.recoveryType === 'unverified_user' 
        ? 'Enter the verification code sent to your email' 
        : 'Enter the password reset code sent to your email';
      case 'password': return 'Choose a new secure password for your account';
      case 'success': return 'Your password has been successfully reset';
      default: return '';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <Card className="border-border shadow-xl">
          <CardHeader className="text-center space-y-2 pb-6">
            <div className="flex items-center justify-center space-x-2">
              {currentStep !== 'email' && currentStep !== 'success' && (
                <button
                  onClick={() => {
                    if (currentStep === 'otp') setCurrentStep('email');
                    if (currentStep === 'password') setCurrentStep('otp');
                  }}
                  className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
              )}
              <div className="flex-1">
                <CardTitle className="text-2xl font-bold text-secondary">
                  {getStepTitle()}
                </CardTitle>
              </div>
            </div>
            <CardDescription className="text-gray-600">
              {getStepDescription()}
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            {currentStep === 'email' && renderEmailStep()}
            {currentStep === 'otp' && renderOTPStep()}
            {currentStep === 'password' && renderPasswordStep()}
            {currentStep === 'success' && renderSuccessStep()}

            {currentStep === 'email' && (
              <div className="text-center text-sm">
                <span className="text-gray-600">Remember your password? </span>
                <Link
                  to="/login"
                  className="text-primary hover:text-blue-700 transition-colors font-medium"
                >
                  Sign in
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
} 